import {
  ICruiseLine,
  IProfile,
  ProfileID,
  TripID,
  TripRoomGuestID,
  TripRoomID,
} from "@cruisen/contract"
import { ModalWrapper } from "../../templates/ModalWrapper"
import CloseModalButton from "../../buttons/CloseModalButton"
import styles from "./styles.module.scss"
import { useForm } from "react-hook-form"
import { colors } from "@theme/colors"
import FormTextInput from "../../atoms/FormTextInput"
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { upsertCruiseLineLoyaltyMember } from "../../../services/firebase-functions/profile"
import { Popover } from "../../atoms/Popover"
import { CruiseLinesList } from "../../atoms/CruiseLinesList"
import { makeAutoObservable } from "mobx"
import { useMemo } from "react"
import { observer } from "mobx-react-lite"
import ChevronDownOutline from "~icons/eva/chevron-down-outline"
import ChevronUpOutline from "~icons/eva/chevron-up-outline"
import { saveProfile } from "../../../services/firebase-functions/profile"

type AddLoyaltyNumberFormValues = {
  cruiseLineId: string
  loyaltyNumber: string
}

const addLoyaltyNumberSchema = yup.object().shape({
  cruiseLineId: yup.string().required("-"),
  loyaltyNumber: yup.string().required("-"),
})

class AddLoyaltyNumberModalState {
  isCruiseLineListOpen: boolean
  selectedCruiseLineName: string

  constructor() {
    this.isCruiseLineListOpen = false
    this.selectedCruiseLineName = ""

    makeAutoObservable(this)
  }

  setCruiseLineListOpen(isOpen: boolean) {
    this.isCruiseLineListOpen = isOpen
  }

  setSelectedCruiseLineName(name: string) {
    this.selectedCruiseLineName = name
  }
}

export const AddLoyaltyNumberModal = observer(
  ({
    cruiseLines,
    tripId,
    roomId,
    closeModal,
    profileId,
    setProfile,
    tripRoomGuestProfile,
    isLoading,
  }: {
    cruiseLines: ICruiseLine[]
    tripId: TripID
    roomId: TripRoomID
    closeModal: () => void
    profileId?: ProfileID
    setProfile: (profile: IProfile) => void
    tripRoomGuestProfile: IProfile & { isDataFilled: boolean; guestId: TripRoomGuestID }
    isLoading: boolean
  }) => {
    const state = useMemo(() => new AddLoyaltyNumberModalState(), [])

    const {
      control,
      handleSubmit,
      formState: { isValid },
      setValue,
    } = useForm({
      defaultValues: {
        cruiseLineId: "",
        loyaltyNumber: "",
      },
      resolver: yupResolver(addLoyaltyNumberSchema),
      mode: "all",
    })

    const queryClient = useQueryClient()

    const { mutate: addLoyaltyNumber, isPending } = useMutation({
      mutationFn: async (data: AddLoyaltyNumberFormValues) => {
        if (profileId) {
          return await upsertCruiseLineLoyaltyMember({
            cruiseLineLoyaltyMember: {
              cruiseLineId: Number(data.cruiseLineId),
              loyaltyCode: data.loyaltyNumber,
              profileId,
            },
            tripId,
          })
        } else {
          const savedProfile = await saveProfile({
            profile: {
              firstName: tripRoomGuestProfile.firstName,
              lastName: tripRoomGuestProfile.lastName,
              ageGroup: tripRoomGuestProfile.ageGroup,
              phone: tripRoomGuestProfile.phone,
              isPrimaryProfile: tripRoomGuestProfile.isPrimaryProfile,
              ownerUserId: tripRoomGuestProfile.ownerUserId,
            },
            tripId,
            tripRoomGuestData: {
              roomId,
              guestId: tripRoomGuestProfile.guestId,
            },
            cruiseLineLoyaltyMember: {
              cruiseLineId: Number(data.cruiseLineId),
              loyaltyCode: data.loyaltyNumber,
            },
          })

          if (savedProfile) {
            setProfile(savedProfile)
          }
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["allProfiles", tripId] })
        closeModal()
      },
    })

    const onSubmit = (data: AddLoyaltyNumberFormValues) => {
      addLoyaltyNumber(data)
    }

    return (
      <ModalWrapper>
        <div className={styles.container}>
          <CloseModalButton onClick={closeModal} className={styles.closeButton} />
          <div className={styles.header}>
            <h2>Add loyalty number</h2>
          </div>

          <div className={styles.formContainer}>
            <Popover
              isOpen={state.isCruiseLineListOpen}
              onClickOutside={() => state.setCruiseLineListOpen(false)}
              content={
                <CruiseLinesList
                  onSelect={(cruiseLineId, cruiseLineName) => {
                    setValue("cruiseLineId", cruiseLineId)
                    state.setSelectedCruiseLineName(cruiseLineName)
                    state.setCruiseLineListOpen(false)
                  }}
                  onClose={() => state.setCruiseLineListOpen(false)}
                  cruiseLines={cruiseLines}
                  loading={isLoading}
                />
              }
            >
              <div className={styles.cruiseLineInput}>
                <FormTextInput
                  control={control}
                  name="cruiseLine"
                  label="Cruise line"
                  value={state.selectedCruiseLineName}
                  onClick={() => state.setCruiseLineListOpen(true)}
                  readOnly
                  parentBackgroundColor={colors.backgroundSecondary}
                  iconRight={
                    state.isCruiseLineListOpen ? <ChevronUpOutline /> : <ChevronDownOutline />
                  }
                />
              </div>
            </Popover>
            <FormTextInput
              control={control}
              name="loyaltyNumber"
              label="Loyalty number"
              parentBackgroundColor={colors.backgroundSecondary}
            />
          </div>
          <div className={styles.buttonContainer}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={closeModal}
              disabled={isPending}
              data-testid="select-cruise-modal-cancel-button"
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.deleteButton}
              disabled={!isValid || isPending}
              onClick={handleSubmit(onSubmit)}
              data-testid="select-cruise-modal-select-button"
            >
              {isPending ? "Adding..." : "Add"}
            </button>
          </div>
        </div>
      </ModalWrapper>
    )
  },
)
